import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { LogOut, CreditCard, Sprout, Star, Crown, Zap, Cloud, Coins, Sparkles, Palette, Menu, ArrowLeft, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useFeatureFlags } from "@/context/FeatureFlagsContext";
import { useT } from "@/i18n";
import Logo from "@/components/Logo";
import Sidebar from "@/components/Sidebar";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { toast } from "sonner";
import DeleteAccountDialog from "@/components/DeleteAccountDialog";

const PLAN_ICON = { free: Sprout, creator: Star, pro: Crown, power: Zap };
const PLAN_COLOR = {
  free: "text-emerald-700 bg-emerald-50 border-emerald-300",
  creator: "text-zinc-800 bg-gradient-to-r from-zinc-100 via-slate-100 to-zinc-200 border-zinc-400",
  pro: "text-amber-900 bg-gradient-to-r from-amber-100 via-yellow-100 to-amber-200 border-amber-400",
  power: "text-purple-900 bg-gradient-to-r from-purple-100 via-fuchsia-100 to-purple-200 border-purple-400",
};
const AVATAR_GRADIENT = {
  free: "from-emerald-400 to-teal-500",
  creator: "from-zinc-500 to-slate-700",
  pro: "from-amber-400 via-orange-500 to-pink-500",
  power: "from-purple-500 via-fuchsia-500 to-pink-500",
};

// Mobile-only top header (md:hidden). Layout:
//   [hamburger] [back?] [Logo] ............ [avatar]
//
// - Hamburger opens the full Sidebar as a left-side drawer so mobile users
//   reach every dashboard section that desktop users see in their sticky
//   sidebar — same items, same order.
// - Back arrow appears whenever we're deeper than the dashboard root, and
//   simply pops the navigation stack (router back).
// - Tapping the avatar opens an account dropdown with user info, plan
//   badge, credits, trial countdown, theme switcher and Sign out.
export default function MobileHeader() {
  const { user, logout } = useAuth();
  const { theme, setTheme, themes } = useTheme();
  const ff = useFeatureFlags?.() || { isHidden: () => false };
  const isHidden = ff.isHidden || (() => false);
  const { t } = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const menuRef = useRef(null);

  // Show the back button on every page EXCEPT the dashboard root and the
  // top-level auth screens. router.back() pops to the previous in-app page
  // they came from — natural mobile pattern, matches what iOS users expect.
  const canGoBack = location.pathname !== "/app" && location.pathname !== "/app/";

  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("touchstart", onDocClick);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("touchstart", onDocClick);
    };
  }, [menuOpen]);

  if (!user) return null;

  const planKey = user?.plan || "free";
  const PlanIcon = PLAN_ICON[planKey] || Sprout;
  const planColorClass = PLAN_COLOR[planKey] || PLAN_COLOR.free;
  const avatarGradient = AVATAR_GRADIENT[planKey] || AVATAR_GRADIENT.free;
  const displayName = user?.name || user?.email?.split("@")[0] || "User";
  const initial = (displayName?.[0] || "U").toUpperCase();

  const handleLogout = async () => {
    setMenuOpen(false);
    await logout();
    navigate("/");
  };

  const handleDeleteAccount = () => {
    setMenuOpen(false);
    setDeleteDialogOpen(true);
  };

  const handleDeleteSuccess = async () => {
    toast.success(
      t("delete_account_ok") ||
        "Your account has been deleted. A confirmation email is on the way."
    );
    await logout();
    setTimeout(() => { window.location.href = "/"; }, 800);
  };

  const goToBilling = () => {
    setMenuOpen(false);
    navigate("/app/billing");
  };

  return (
    <header
      className="md:hidden sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-zinc-200"
      data-testid="mobile-header"
    >
      <div className="flex items-center justify-between px-3 h-14 gap-1">
        {/* Left cluster: hamburger + back + logo */}
        <div className="flex items-center gap-1 min-w-0 flex-1">
          <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
            <SheetTrigger asChild>
              <button
                type="button"
                className="w-9 h-9 inline-flex items-center justify-center rounded-md hover:bg-zinc-100 active:bg-zinc-200 transition-colors shrink-0"
                data-testid="mobile-header-menu-btn"
                aria-label="Open menu"
              >
                <Menu className="w-5 h-5 text-zinc-700" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-[280px] sm:w-[300px]" data-testid="mobile-header-drawer">
              <Sidebar inDrawer onNavigate={() => setDrawerOpen(false)} />
            </SheetContent>
          </Sheet>

          {canGoBack && (
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="w-9 h-9 inline-flex items-center justify-center rounded-md hover:bg-zinc-100 active:bg-zinc-200 transition-colors shrink-0"
              data-testid="mobile-header-back-btn"
              aria-label="Go back"
            >
              <ArrowLeft className="w-5 h-5 text-zinc-700" />
            </button>
          )}

          <div className="min-w-0 ml-1">
            <Logo size="sm" />
          </div>
        </div>

        {/* Right cluster: avatar */}
        <div className="relative shrink-0" ref={menuRef}>
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            data-testid="mobile-header-avatar-btn"
            aria-label="Open profile menu"
            aria-expanded={menuOpen}
            className="flex items-center gap-2 group"
          >
            {user?.picture ? (
              <img
                src={user.picture}
                alt=""
                referrerPolicy="no-referrer"
                className={`w-9 h-9 rounded-full object-cover ring-2 ring-offset-1 ring-offset-white transition-all ${
                  menuOpen ? "ring-zinc-900" : "ring-zinc-200 group-active:ring-zinc-400"
                }`}
              />
            ) : (
              <div
                className={`w-9 h-9 rounded-full bg-gradient-to-br ${avatarGradient} text-white font-black text-sm flex items-center justify-center ring-2 ring-offset-1 ring-offset-white transition-all ${
                  menuOpen ? "ring-zinc-900" : "ring-zinc-200 group-active:ring-zinc-400"
                }`}
                data-testid="mobile-header-avatar-fallback"
              >
                {initial}
              </div>
            )}
          </button>

          {menuOpen && (
            <div
              className="absolute right-0 top-12 w-72 rounded-xl border border-zinc-200 bg-white shadow-2xl overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150"
              data-testid="mobile-header-profile-menu"
            >
              <div className="px-4 pt-4 pb-3 bg-gradient-to-br from-zinc-50 to-white border-b border-zinc-200">
                <div className="flex items-center gap-3">
                  {user?.picture ? (
                    <img src={user.picture} alt="" referrerPolicy="no-referrer" className="w-12 h-12 rounded-full object-cover ring-2 ring-white shadow" />
                  ) : (
                    <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${avatarGradient} text-white font-black text-lg flex items-center justify-center ring-2 ring-white shadow`}>
                      {initial}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-bold text-zinc-900 truncate" data-testid="mobile-header-user-name">{displayName}</div>
                    <div className="text-[11px] text-zinc-500 truncate" data-testid="mobile-header-user-email">{user?.email}</div>
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={goToBilling}
                data-testid="mobile-header-plan-badge"
                className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left border-b border-zinc-100 hover:bg-zinc-50 transition-colors"
              >
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-[11px] font-black uppercase tracking-wider ${planColorClass}`}>
                  <PlanIcon className="w-3.5 h-3.5" />
                  {planKey}
                  {user?.is_admin && (
                    <span
                      className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-wider text-white shadow-sm"
                      style={{ background: "linear-gradient(135deg, #00A1E0 0%, #003e6b 100%)" }}
                    >
                      <Cloud className="w-2.5 h-2.5" />
                      ADMIN
                    </span>
                  )}
                </span>
                <span className="text-[11px] font-semibold text-zinc-500 inline-flex items-center gap-1">
                  <CreditCard className="w-3.5 h-3.5" />
                  {t("side_upgrade") || "Upgrade"} →
                </span>
              </button>

              <button
                type="button"
                onClick={goToBilling}
                data-testid="mobile-header-credits-row"
                className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left border-b border-zinc-100 hover:bg-zinc-50 transition-colors"
              >
                <span className="inline-flex items-center gap-2 text-[12px] font-semibold text-zinc-700">
                  <Coins className="w-4 h-4 text-amber-500" />
                  {t("side_credits") || "Credits"}
                </span>
                <span className="text-[13px] font-black text-zinc-900" data-testid="mobile-header-credits-value">
                  {typeof user?.credits === "number" ? user.credits.toLocaleString() : "—"}
                </span>
              </button>

              {user?.is_trial && (
                <button
                  type="button"
                  onClick={() => { setMenuOpen(false); navigate("/app/pricing"); }}
                  data-testid="mobile-header-trial-row"
                  className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left border-b border-zinc-100 text-white"
                  style={{
                    background: (user.trial_days_remaining ?? 0) <= 2
                      ? "linear-gradient(135deg, #dc2626 0%, #f97316 100%)"
                      : "linear-gradient(135deg, #f59e0b 0%, #ec4899 100%)",
                  }}
                >
                  <span className="inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-wider">
                    <Sparkles className="w-3.5 h-3.5" />
                    {t("trial_pill_label", { days: user.trial_days_remaining ?? 0 })}
                  </span>
                  <span className="text-[11px] font-semibold opacity-90">{t("trial_pill_cta") || "Upgrade"} →</span>
                </button>
              )}

              {(() => {
                const visibleThemes = (themes || []).filter((th) => !isHidden(th.flag));
                if (visibleThemes.length === 0) return null;
                return (
                  <div className="px-4 py-3 border-b border-zinc-100">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 mb-2 inline-flex items-center gap-1.5">
                      <Palette className="w-3.5 h-3.5" />
                      {t("side_theme") || "Theme"}
                    </div>
                    <div className="flex items-center gap-2" data-testid="mobile-header-theme-switcher">
                      {visibleThemes.map((th) => (
                        <button
                          key={th.id}
                          type="button"
                          onClick={() => setTheme(th.id)}
                          data-testid={`mobile-header-theme-${th.id}`}
                          className={`w-7 h-7 rounded-full border-2 transition-all ${theme === th.id ? "border-zinc-900 scale-110 shadow-md" : "border-zinc-300 active:scale-95"}`}
                          style={{ background: th.swatch }}
                          title={th.label}
                          aria-label={th.label}
                        />
                      ))}
                    </div>
                  </div>
                );
              })()}

              <button
                type="button"
                onClick={handleLogout}
                data-testid="mobile-header-logout-btn"
                className="w-full flex items-center gap-3 px-4 py-3 text-sm font-semibold text-red-600 hover:bg-red-50 transition-colors"
              >
                <LogOut className="w-4 h-4" />
                {t("side_logout") || "Sign out"}
              </button>

              {/* Apple 5.1.1(v) — account deletion must be reachable from
                  inside the app. Placed BELOW Sign out (destructive, less
                  frequently tapped) with a subdued style so it doesn't
                  compete with primary CTAs. */}
              <button
                type="button"
                onClick={handleDeleteAccount}
                data-testid="mobile-header-delete-account-btn"
                className="w-full flex items-center gap-3 px-4 py-3 text-xs font-semibold text-zinc-500 hover:bg-zinc-50 border-t border-zinc-100 transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
                {t("delete_account") || "Delete my account"}
              </button>
            </div>
          )}
        </div>
      </div>
      <DeleteAccountDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onSuccess={handleDeleteSuccess}
      />
    </header>
  );
}
